// src/components/sections/FinancingSection.jsx
import React, { useMemo } from "react";
import { Calculator } from "lucide-react";
import FormattedNumberInput from "../FormattedNumberInput";
import FieldHelpLabel from "../FieldHelpLabel";
import { useLanguage } from "../../contexts/LanguageContext";
import { remainingMonthsFromPaymentCanadianAmortizing } from "../../utils/canadianMortgagePayment";

export default function FinancingSection({
  financing = {},
  onChange,
}) {
  const { t } = useLanguage();

  const assumptionDerived = useMemo(() => {
    const principal = parseFloat(financing.assumedLoanBalance) || 0;
    const ratePct = parseFloat(financing.mortgageRate) || 0;
    const payment = parseFloat(financing.assumptionStatedMonthlyPayment) || 0;
    if (principal <= 0 || payment <= 0 || ratePct <= 0) {
      return { months: null, paymentTooLow: false };
    }
    const months = remainingMonthsFromPaymentCanadianAmortizing(
      principal,
      ratePct / 100,
      payment,
    );
    return {
      months,
      paymentTooLow: months === null,
    };
  }, [financing.assumedLoanBalance, financing.mortgageRate, financing.assumptionStatedMonthlyPayment]);

  const handleChange = (field, value) => {
    onChange({ ...financing, [field]: value }, field);
  };

  const showStandardRateGrid = financing.financingType !== "assumption";
  const isPrivate = financing.financingType === "private";

  return (
    <div className="border rounded-lg p-4 sm:p-6">
      <h2 className="text-lg font-semibold mb-4 text-purple-600 flex items-center">
        <Calculator className="w-5 h-5 mr-2" />
        {t("financing.title")}
      </h2>
      <div>
        <FieldHelpLabel help={t("financing.typeHelp")}>{t("financing.type")}</FieldHelpLabel>
        <select
          value={financing.financingType || "conventional"}
          onChange={(e) => handleChange("financingType", e.target.value)}
          className="w-full border rounded p-2"
        >
          <option value="conventional">{t("financing.type.conventional")}</option>
          <option value="cmhc">{t("financing.type.cmhc")}</option>
          <option value="cmhc_aph">{t("financing.type.cmhc_aph")}</option>
          <option value="private">{t("financing.type.private")}</option>
          <option value="assumption">{t("financing.type.assumption")}</option>
        </select>
      </div>

      {financing.financingType === "assumption" && (
        <div className="mt-4 space-y-4">
          <div>
            <FieldHelpLabel help={t("financing.assumedLoanBalanceHelp")}>
              {t("financing.assumedLoanBalance")}
            </FieldHelpLabel>
            <FormattedNumberInput
              value={financing.assumedLoanBalance || ""}
              onChange={(val) => handleChange("assumedLoanBalance", val)}
              className="w-full border rounded p-2"
              placeholder="0"
              type="currency"
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <FieldHelpLabel help={t("financing.assumptionMonthlyPaymentHelp")}>
                {t("financing.assumptionMonthlyPayment")}
              </FieldHelpLabel>
              <FormattedNumberInput
                value={financing.assumptionStatedMonthlyPayment || ""}
                onChange={(val) => handleChange("assumptionStatedMonthlyPayment", val)}
                className="w-full border rounded p-2"
                placeholder="0"
                type="currency"
              />
            </div>
            <div>
              <FieldHelpLabel help={t("financing.contractualRateHelp")}>
                {t("financing.contractualRate")}
              </FieldHelpLabel>
              <FormattedNumberInput
                value={financing.mortgageRate || ""}
                onChange={(val) => handleChange("mortgageRate", val)}
                className="w-full border rounded p-2"
                placeholder="5.5"
                type="percentage"
              />
            </div>
          </div>
          <div>
            <FieldHelpLabel help={t("financing.assumptionTermEndHint")}>
              {t("financing.assumptionTermEndOptional")}
            </FieldHelpLabel>
            <input
              type="date"
              value={financing.assumptionTermEnd || ""}
              onChange={(e) => handleChange("assumptionTermEnd", e.target.value)}
              className="w-full border rounded p-2 text-sm"
            />
          </div>
          <div className="text-sm">
            <FieldHelpLabel help={t("financing.assumptionDerivedAmortMonthsHint")}>
              {t("financing.assumptionDerivedAmortMonths")}
            </FieldHelpLabel>
            {assumptionDerived.paymentTooLow ? (
              <span className="text-amber-700 font-medium">{t("financing.assumptionPaymentTooLow")}</span>
            ) : assumptionDerived.months != null ? (
              <span className="font-semibold text-gray-900">
                {new Intl.NumberFormat(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                }).format(assumptionDerived.months)}
              </span>
            ) : (
              <span className="text-gray-500">—</span>
            )}
          </div>
        </div>
      )}

      {financing.financingType === "cmhc_aph" && (
        <div className="mt-4">
          <FieldHelpLabel help={t("financing.aphPointsHelp")}>{t("financing.aphPoints")}</FieldHelpLabel>
          <FormattedNumberInput
            value={financing.aphPoints || ""}
            onChange={(val) => handleChange("aphPoints", val)}
            className="w-full border rounded p-2"
            placeholder="0"
          />
        </div>
      )}

      {showStandardRateGrid && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 items-start">
          <div className="order-1">
            <FieldHelpLabel help={t("financing.contractualRateHelp")}>
              {t("financing.contractualRate")}
            </FieldHelpLabel>
            <FormattedNumberInput
              value={financing.mortgageRate || ""}
              onChange={(val) => handleChange("mortgageRate", val)}
              className="w-full border rounded p-2"
              placeholder="5.5"
              type="percentage"
            />
          </div>
          {isPrivate ? (
            <div className="order-2">
              <FieldHelpLabel help={t("financing.ltvRatioHelp")}>{t("financing.ltvRatio")}</FieldHelpLabel>
              <FormattedNumberInput
                value={financing.ltvRatio || ""}
                onChange={(val) => handleChange("ltvRatio", val)}
                className="w-full border rounded p-2"
                placeholder="60"
                type="percentage"
              />
            </div>
          ) : (
            <div className="order-3 sm:order-2">
              <FieldHelpLabel help={t("financing.amortizationHelp")}>
                {t("financing.amortization")}
              </FieldHelpLabel>
              <FormattedNumberInput
                value={financing.amortization || ""}
                onChange={(val) => handleChange("amortization", val)}
                className="w-full border rounded p-2"
                placeholder="25"
              />
            </div>
          )}
          <div className={isPrivate ? "order-3" : "order-4 sm:order-3"}>
            <FieldHelpLabel help={t("financing.termHelp")}>{t("financing.term")}</FieldHelpLabel>
            <select
              value={financing.term || ""}
              onChange={(e) => handleChange("term", e.target.value)}
              className="w-full border rounded p-2"
            >
              <option value="1">1 an</option>
              <option value="2">2 ans</option>
              <option value="3">3 ans</option>
              <option value="5">5 ans</option>
              <option value="10">10 ans</option>
            </select>
          </div>
          {!isPrivate && (
            <>
              <div className="order-2 sm:order-4">
                <FieldHelpLabel help={t("financing.qualificationRateHelp")}>
                  {t("financing.qualificationRate")}
                </FieldHelpLabel>
                <FormattedNumberInput
                  value={financing.qualificationRate || ""}
                  onChange={(val) => handleChange("qualificationRate", val)}
                  className="w-full border rounded p-2"
                  placeholder="6.0"
                  type="percentage"
                />
              </div>
              <div className="order-5">
                <FieldHelpLabel help={t("financing.debtCoverageRatioHelp")}>
                  {t("financing.debtCoverageRatio")}
                </FieldHelpLabel>
                <FormattedNumberInput
                  value={financing.debtCoverageRatio || ""}
                  onChange={(val) => handleChange("debtCoverageRatio", val)}
                  className="w-full border rounded p-2"
                  placeholder="1.15"
                />
              </div>
              <div className="order-6">
                <FieldHelpLabel help={t("financing.financingCapRateHelp")}>
                  {t("financing.financingCapRate")}
                </FieldHelpLabel>
                <FormattedNumberInput
                  value={financing.financingCapRate || ""}
                  onChange={(val) => handleChange("financingCapRate", val)}
                  className="w-full border rounded p-2"
                  placeholder=""
                  type="percentage"
                />
              </div>
            </>
          )}
        </div>
      )}

      {isPrivate && (
        <div className="mt-4">
          <FieldHelpLabel help={t("financing.originationFeeHelp")}>
            {t("financing.originationFee")}
          </FieldHelpLabel>
          <div className="flex">
            <FormattedNumberInput
              value={financing.originationFee || ""}
              onChange={(val) => handleChange("originationFee", val)}
              className="w-full border rounded p-2"
              placeholder="0"
              type={financing.originationFeeType === "currency" ? "currency" : "percentage"}
            />
            <select
              value={financing.originationFeeType || "percentage"}
              onChange={(e) => handleChange("originationFeeType", e.target.value)}
              className="ml-2 border rounded p-2"
            >
              <option value="percentage">%</option>
              <option value="currency">$</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
